import { Alert, Button, List, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { SectionCard } from "../../components/ui";
import { Page } from "../../shell/Page";

/**
 * Reports — requirements not yet defined.
 *
 * Deliberately empty rather than guessed at. Inventing a set of reports would
 * mean building screens nobody asked for, and every one of them would be a
 * claim about what the business measures - which is exactly the thing still
 * to be decided.
 *
 * What it does do is say what is already recorded, because that is the part
 * that constrains the answer: a report can only ever show what the data
 * holds, and it is much cheaper to find a gap now than after the reports are
 * specified.
 */
export function Reports() {
  const navigate = useNavigate();

  return (
    <Page title="Reports" subtitle="Not defined yet">
      <Alert
        variant="light"
        color="yellow"
        radius="md"
        icon={<IconAlertTriangle size={18} stroke={1.8} />}
      >
        No reports have been specified yet. This section is a placeholder until
        the requirements are given.
      </Alert>

      <SectionCard title="What is already recorded">
        <Stack gap="sm">
          <Text fz="md" c="gray.7">
            A report can only show what the data holds, so this is what is
            available to build from today. If something you want to measure is
            not here, it is worth saying so before the reports are specified —
            adding it later may mean a schema change.
          </Text>
          <List spacing="xs" fz="md" c="gray.7">
            <List.Item>
              <strong>Sales</strong> — every day order, order and GST bill, with
              per-line quantity, unit price, discount and the CGST/SGST/IGST
              split, by customer and by date.
            </List.Item>
            <List.Item>
              <strong>Collections</strong> — every payment against a bill, its
              method, its voucher type (cash book or bank book), who collected
              it and where.
            </List.Item>
            <List.Item>
              <strong>Inventory</strong> — an append-only movement ledger with a
              running balance per product, so stock on any past date is
              reconstructable rather than only current.
            </List.Item>
            <List.Item>
              {/* Warranty was in this list, and it should not be: the business
                  had that logic removed, so nothing records a warranty
                  judgement any more and a report built on it would have a
                  column that is UNKNOWN on every recent row. What IS recorded
                  is the outcome and the pro-rata amount. */}
              <strong>Service</strong> — every job with its outcome (OK, FOC,
              ProRata or Rejected), the pro-rata amount where one was agreed,
              and the time between collection, diagnosis and completion.
            </List.Item>
            <List.Item>
              <strong>Delivery</strong> — who carried what, when it was handed
              over, what was collected at the door, and every failed attempt
              with its reason.
            </List.Item>
            <List.Item>
              <strong>Audit</strong> — who did what, and when, across all of the
              above.
            </List.Item>
          </List>
        </Stack>
      </SectionCard>

      <SectionCard title="In the meantime">
        <Stack gap="sm">
          <Text fz="md" c="gray.7">
            Some of this is already readable in the app. Every one of these
            lists — and every other grid — now has an <strong>Export</strong>
            button giving the whole list as a spreadsheet, so a question like
            &ldquo;how many FOC outcomes last month&rdquo; can be answered
            today without a report being specified first.
          </Text>
          <Button variant="default" onClick={() => navigate("/sales/bills")}>
            Bills raised
          </Button>
          <Button variant="default" onClick={() => navigate("/inventory/movements")}>
            Stock movement ledger
          </Button>
          <Button variant="default" onClick={() => navigate("/admin/audit")}>
            Audit trail
          </Button>
        </Stack>
      </SectionCard>
    </Page>
  );
}
